import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { ArrowLeft, Calendar, User, Phone, Mail, FileText, CheckCircle, X, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const ReferralDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [referral, setReferral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => {
    fetchReferralDetail();
  }, [id]);

  const fetchReferralDetail = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/${id}`, {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      const data = await response.json();
      if (data.success) {
        setReferral(data.referral);
      }
    } catch (error) {
      console.error('Error fetching referral:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/${id}/accept`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      const data = await response.json();
      if (data.success) {
        alert('Referral accepted! Patient will be notified.');
        setShowAcceptModal(false);
        fetchReferralDetail();
      }
    } catch (error) {
      console.error('Error accepting referral:', error);
      alert('Error accepting referral');
    }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) {
      alert('Please provide a reason for rejection');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/referrals/${id}/reject`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ reason: rejectReason })
      });
      const data = await response.json();
      if (data.success) {
        alert('Referral rejected. Referring doctor will be notified.');
        setShowRejectModal(false);
        fetchReferralDetail();
      }
    } catch (error) {
      console.error('Error rejecting referral:', error);
      alert('Error rejecting referral');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      scheduled: 'bg-blue-100 text-blue-800 border-blue-200',
      in_progress: 'bg-purple-100 text-purple-800 border-purple-200',
      completed: 'bg-green-100 text-green-800 border-green-200',
      rejected: 'bg-red-100 text-red-800 border-red-200'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading referral details...</p>
        </div>
      </div>
    );
  }

  if (!referral) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Referral Not Found</h2>
          <Button onClick={() => navigate('/referrals')}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back to Referrals
          </Button>
        </div>
      </div>
    );
  }

  const canAcceptReject = user?.role === 'admin' || user?.role === 'superadmin';
  const isPending = referral.status === 'pending';

  return (
    <div className="max-w-4xl mx-auto">
      <Button variant="ghost" onClick={() => navigate(user?.role === 'patient' ? '/dashboard' : '/referrals')} className="mb-6">
        <ArrowLeft className="h-4 w-4 mr-2" />Back to {user?.role === 'patient' ? 'Dashboard' : 'Referrals'}
      </Button>

      <Card className="mb-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-2xl mb-2">Referral Details</CardTitle>
              <p className="text-sm text-gray-600">Referral ID: {referral.referral_id || referral.id}</p>
            </div>
            <div className="flex items-center space-x-2">
              <Badge className={getStatusColor(referral.status)}>
                {referral.status?.replace('_', ' ').toUpperCase()}
              </Badge>
              {isPending && canAcceptReject && (
                <>
                  <Button size="sm" className="bg-green-600 text-white hover:bg-green-700" onClick={() => setShowAcceptModal(true)}>
                    <CheckCircle className="h-4 w-4 mr-2" />Accept
                  </Button>
                  <Button size="sm" variant="outline" className="border-red-500 text-red-500 hover:bg-red-50" onClick={() => setShowRejectModal(true)}>
                    <X className="h-4 w-4 mr-2" />Reject
                  </Button>
                </>
              )}
            </div>
          </div>
        </CardHeader>
      </Card>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader><CardTitle className="flex items-center"><User className="h-5 w-5 mr-2 text-[#D4AF37]" />Patient Information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><p className="text-sm text-gray-600">Name</p><p className="font-semibold text-[#0A1628]">{referral.patient_name}</p></div>
            <div><p className="text-sm text-gray-600">Email</p><p className="text-gray-700">{referral.patient_email}</p></div>
            <div><p className="text-sm text-gray-600">Phone</p><p className="text-gray-700">{referral.patient_phone}</p></div>
            {referral.patient_dob && <div><p className="text-sm text-gray-600">Date of Birth</p><p className="text-gray-700">{referral.patient_dob}</p></div>}
          </CardContent>
        </Card>

        <Card>
          <CardHeader><CardTitle className="flex items-center"><FileText className="h-5 w-5 mr-2 text-[#D4AF37]" />Referral Information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div><p className="text-sm text-gray-600">Procedure</p><p className="font-semibold text-[#0A1628]">{referral.procedure}</p></div>
            <div><p className="text-sm text-gray-600">Urgency</p><Badge variant="outline">{referral.urgency}</Badge></div>
            <div><p className="text-sm text-gray-600">Referring Doctor</p><p className="text-gray-700">{referral.referring_doctor_name}</p></div>
            <div><p className="text-sm text-gray-600">Practice</p><p className="text-gray-700">{referral.referring_practice}</p></div>
            {referral.scheduled_date && <div><p className="text-sm text-gray-600">Scheduled Date</p><p className="text-[#D4AF37] font-semibold">{new Date(referral.scheduled_date).toLocaleString()}</p></div>}
          </CardContent>
        </Card>

        <Card className="lg:col-span-2">
          <CardHeader><CardTitle>Medical Information</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            {referral.insurance && <div><p className="text-sm text-gray-600">Insurance</p><p className="text-gray-700">{referral.insurance}</p>{referral.insurance_id && <p className="text-xs text-gray-500">ID: {referral.insurance_id}</p>}</div>}
            {referral.medical_history && <div><p className="text-sm text-gray-600">Medical History</p><p className="text-gray-700 whitespace-pre-wrap">{referral.medical_history}</p></div>}
            <div><p className="text-sm text-gray-600">Notes</p><p className="text-gray-700 whitespace-pre-wrap">{referral.notes || 'No notes'}</p></div>
          </CardContent>
        </Card>
      </div>

      {showAcceptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Accept Referral</h2>
            <p className="text-gray-700 mb-6">Are you sure you want to accept this referral for {referral.patient_name}?</p>
            <p className="text-sm text-gray-600 mb-6">The referring doctor and patient will be notified.</p>
            <div className="flex space-x-2">
              <Button className="flex-1 bg-green-600 text-white hover:bg-green-700" onClick={handleAccept}>
                <CheckCircle className="h-4 w-4 mr-2" />Confirm Accept
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowAcceptModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}

      {showRejectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-2xl font-bold text-[#0A1628] mb-4">Reject Referral</h2>
            <p className="text-gray-700 mb-4">Please provide a reason for rejecting this referral:</p>
            <Textarea placeholder="Reason for rejection..." rows={4} value={rejectReason} onChange={(e) => setRejectReason(e.target.value)} className="mb-4" />
            <p className="text-sm text-gray-600 mb-6">The referring doctor will be notified with this reason.</p>
            <div className="flex space-x-2">
              <Button className="flex-1 bg-red-600 text-white hover:bg-red-700" onClick={handleReject}>
                <X className="h-4 w-4 mr-2" />Confirm Reject
              </Button>
              <Button variant="outline" className="flex-1" onClick={() => setShowRejectModal(false)}>Cancel</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralDetail;